import { useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { languageToOrgLocale } from '../../../i18n/locale';
import type { Language } from '../../../i18n/translations';
import { trackEvent } from '../../../lib/analytics';
import { settingsAPI } from '../../../lib/api';
import { useMixpanel } from '../../../lib/mixpanel';
import { Sentry } from '../../../lib/sentry';

type OrgMembership = {
  organization?: { id: string };
  organizationId?: string;
  role: string;
};

export function useLanguageSync() {
  const { language, setLanguage } = useTranslation();
  const { backendUser, currentOrganization } = useAuth();
  const mp = useMixpanel();

  const changeLanguage = useCallback(
    async (nextLanguage: Language) => {
      await setLanguage(nextLanguage);

      const orgId = currentOrganization?.id;
      if (!orgId) {
        return;
      }

      const memberships: OrgMembership[] =
        ((backendUser as unknown as Record<string, unknown>)
          ?.organizationMembers as OrgMembership[]) ??
        ((backendUser as unknown as Record<string, unknown>)?.organizations as OrgMembership[]) ??
        [];

      const membership = memberships.find(
        (o) => (o.organization?.id ?? o.organizationId) === orgId,
      );

      if (membership?.role !== 'owner') {
        return;
      }

      const action = 'org.settings_updated';
      const baseFields = {
        action,
        organizationId: orgId,
        userId: (backendUser as { id?: string } | null)?.id,
        setting: 'locale',
        locale: languageToOrgLocale(nextLanguage),
      };

      Sentry.addBreadcrumb({
        category: 'org',
        message: action,
        level: 'info',
        data: baseFields,
      });

      try {
        await settingsAPI.updateOrganization(orgId, {
          locale: languageToOrgLocale(nextLanguage),
        });
        console.info(action, { ...baseFields, outcome: 'success' });
        trackEvent(mp, 'org.settings_updated', { setting: 'locale' });
        Sentry.metrics?.increment?.(action, 1, { tags: { outcome: 'success' } });
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'org',
          message: 'settings_updated.failed',
          level: 'error',
          data: { ...baseFields, error: (error as Error).message },
        });
        console.warn('Unable to sync organization locale:', error);
        Sentry.metrics?.increment?.(action, 1, { tags: { outcome: 'error' } });
      }
    },
    [setLanguage, backendUser, currentOrganization, mp],
  );

  return { language, changeLanguage };
}
